import { Box, ThemeProvider, useTheme } from '@mui/material';
import { ExpandMoreOutlined } from '@mui/icons-material';
import { PaloThemeProvider, tokens } from '@synergy/theme-palo';
import { ReactElement, ReactNode, createContext, useContext, useState } from 'react';

const c = tokens.color;

/**
 * Where the dock sits. The Belegmaske wants it on the right: the purchasing and
 * administration users read the notes while they work the document, and a panel
 * they have to scroll down to is one they miss (#2184).
 */
export type PaloDockPlacement = 'bottom' | 'right';

const PaloDockPlacementContext = createContext<PaloDockPlacement>('bottom');

/**
 * Set by the chrome that owns the layout, because only it can turn the body and
 * the dock into columns. The dock reads the placement from here so a mask never
 * has to state it twice and the two halves cannot drift apart.
 */
export const PaloDockPlacementProvider = PaloDockPlacementContext.Provider;

/**
 * Where the panel around this content sits. A panel head built for the full
 * width of the window has to fold itself once it stands in a narrow column.
 */
export const usePaloDockPlacement = (): PaloDockPlacement => useContext(PaloDockPlacementContext);

export interface PaloDockPanel {
    key: string;
    label: ReactNode;
    /** Optional count shown next to the label. */
    badge?: ReactNode;
    content: ReactNode;
}

export interface PaloActivityDockProps {
    panels: PaloDockPanel[];
    defaultOpen?: boolean;
    /** Height of the expanded content area, when docked at the bottom. */
    height?: number;
    /** Width of the expanded panel, when docked at the right. */
    width?: number;
    /**
     * Which panel to show, when the mask wants a say. Only the active panel is
     * mounted, so a shortcut that belongs to a panel is unreachable while
     * another one is up — this is how the mask brings it back.
     *
     * Leave both out and the dock keeps its own tab state as before.
     */
    activeKey?: string;
    onActiveKeyChange?: (key: string) => void;
}

/** Collapsed width of the right-hand dock: the rail that brings it back. */
const RAIL_WIDTH = 32;

/**
 * Activity panel per the prototype's `.act-bar` / `.act-body`: a PALO tab strip
 * with a collapse toggle next to a scrollable content area. The chrome renders
 * under the PALO theme; panel content renders under the ambient theme, so
 * legacy detail panels (activities, notes, transitions) stay intact.
 *
 * Docks at the bottom on its own, and against the right edge when the chrome
 * around it asks for that — see PaloDockPlacementProvider.
 */
export const PaloActivityDock = ({
    panels,
    defaultOpen = true,
    height = 240,
    width = 380,
    activeKey: controlledKey,
    onActiveKeyChange,
}: PaloActivityDockProps): ReactElement => {
    const ambientTheme = useTheme();
    const placement = useContext(PaloDockPlacementContext);
    const [open, setOpen] = useState(defaultOpen);
    const [ownKey, setOwnKey] = useState(panels[0]?.key);

    const activeKey = controlledKey ?? ownKey;
    const activePanel = panels.find(panel => panel.key === activeKey) ?? panels[0];
    const atRight = placement === 'right';

    const show = (key: string) => {
        setOwnKey(key);
        onActiveKeyChange?.(key);
        setOpen(true);
    };

    const tab = (panel: PaloDockPanel): ReactElement => {
        const active = panel.key === activeKey && open;

        return (
            <Box
                key={panel.key}
                role="tab"
                aria-selected={active}
                onClick={() => show(panel.key)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    fontSize: '12px',
                    borderRadius: tokens.radius.small + 'px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    color: active ? c.ink : c.ink2,
                    fontWeight: active ? 600 : 400,
                    backgroundColor: active ? c.surface : 'transparent',
                    boxShadow: active ? tokens.shadow.s1 : undefined,
                    '&:hover': active ? undefined : { color: c.ink },
                }}
            >
                {panel.label}
                {panel.badge != null && panel.badge !== 0 && (
                    <Box
                        component="span"
                        sx={{
                            fontSize: '10px',
                            color: c.ink3,
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {panel.badge}
                    </Box>
                )}
            </Box>
        );
    };

    const toggle = (
        <Box
            role="button"
            aria-label={open ? 'Einklappen' : 'Ausklappen'}
            onClick={() => setOpen(value => !value)}
            sx={{
                marginLeft: atRight ? undefined : 'auto',
                fontSize: '11px',
                color: c.ink3,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                flexShrink: 0,
                '&:hover': { color: c.ink },
                '& svg': {
                    fontSize: 16,
                    transition: 'transform .15s',
                    // Bottom: the chevron points the way the panel will move.
                    // Right: it points at the edge the panel folds against.
                    transform: atRight ? (open ? 'rotate(-90deg)' : 'rotate(90deg)') : open ? 'none' : 'rotate(180deg)',
                },
            }}
        >
            {!atRight && (open ? 'Einklappen' : 'Ausklappen')}
            <ExpandMoreOutlined />
        </Box>
    );

    const content = (
        <ThemeProvider theme={ambientTheme}>
            <Box
                sx={
                    atRight
                        ? {
                              flex: 1,
                              minWidth: 0,
                              minHeight: 0,
                              overflowY: 'auto',
                              // Never sideways: the panels were written for the
                              // width of the window, and a column that scrolls in
                              // both directions hides the start of every line.
                              overflowX: 'hidden',
                              padding: '9px 13px',
                              '& *': { minWidth: 0 },
                              wordBreak: 'break-word',
                          }
                        : { height, overflowY: 'auto', padding: '9px 13px', borderTop: `1px solid ${c.line}` }
                }
            >
                {activePanel?.content}
            </Box>
        </ThemeProvider>
    );

    if (atRight) {
        return (
            <Box
                sx={{
                    flexShrink: 0,
                    width: open ? width : RAIL_WIDTH,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    borderLeft: `1px solid ${c.line}`,
                    backgroundColor: c.surface,
                }}
            >
                <PaloThemeProvider>
                    {open ? (
                        <Box
                            sx={{
                                minHeight: 34,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '3px',
                                padding: '0 8px 0 10px',
                                borderBottom: `1px solid ${c.line}`,
                                backgroundColor: c.surfaceAlt,
                                userSelect: 'none',
                                overflowX: 'auto',
                            }}
                        >
                            {panels.map(tab)}
                            <Box sx={{ marginLeft: 'auto', pl: '4px' }}>{toggle}</Box>
                        </Box>
                    ) : (
                        // Collapsed to a rail: the labels stay readable sideways,
                        // so a panel is one click away instead of two.
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '7px 0',
                                borderBottom: `1px solid ${c.line}`,
                                backgroundColor: c.surfaceAlt,
                                userSelect: 'none',
                            }}
                        >
                            {toggle}
                            {panels.map(panel => (
                                <Box
                                    key={panel.key}
                                    role="tab"
                                    aria-selected={false}
                                    onClick={() => show(panel.key)}
                                    sx={{
                                        writingMode: 'vertical-rl',
                                        fontSize: '11.5px',
                                        color: c.ink2,
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        '&:hover': { color: c.ink },
                                    }}
                                >
                                    {panel.label}
                                </Box>
                            ))}
                        </Box>
                    )}
                </PaloThemeProvider>

                {open && content}
            </Box>
        );
    }

    return (
        <Box sx={{ flexShrink: 0, backgroundColor: c.surface }}>
            <PaloThemeProvider>
                <Box
                    sx={{
                        height: 34,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px',
                        padding: '0 13px',
                        borderTop: `1px solid ${c.line}`,
                        backgroundColor: c.surfaceAlt,
                        userSelect: 'none',
                    }}
                >
                    {panels.map(tab)}
                    {toggle}
                </Box>
            </PaloThemeProvider>

            {open && content}
        </Box>
    );
};
